import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  OFFER_LETTER_BODY_MAX,
  offerLetterSchema,
  unknownMergeFields,
  type OfferLetterInput,
  type OfferLetterRequest,
  type OfferLetterView,
  type OfferMergeField,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Field } from '../../../../components/Field';
import { Input, Textarea } from '../../../../components/Input';
import { Select } from '../../../../components/Select';
import { Switch } from '../../../../components/Switch';
import { Tabs } from '../../../../components/Tabs';
import { errorMessage } from '../../../../lib/api-client';
import { formatDateTime } from '../../../../lib/format';
import { useMediaQuery } from '../../../../lib/use-media-query';
import { useToasts } from '../../../../stores/toasts';
import { useVentures } from '../../../organisation/api/organisation-api';
import { useSaveOfferLetter } from '../../api/offers-api';
import { InsertFieldMenu } from './InsertFieldMenu';
import { LetterPreview } from './LetterPreview';
import { changedLetterParts, formOf, insertField, NEW_LETTER, rebasedLetter, savedLetterOf, type LetterForm, type WatchedLetter } from './offer-letter-form';
import styles from './LetterEditor.module.css';

/** At this width and below the A4 page moves behind a "Preview" tab (the drawing's 1024). */
const ONE_COLUMN_QUERY = '(max-width: 1180px)';
const EVERY_VENTURE = '';

type Pane = 'write' | 'preview';

const PANES = [
  { value: 'write', label: 'Write' },
  { value: 'preview', label: 'Preview' },
] as const;

/** A letter's draft, kept while another letter is open, so switching never throws away what was typed. */
export interface StashedLetter {
  readonly version: number;
  readonly base: LetterForm;
  readonly values: LetterForm;
}

interface Base {
  /** The version a save is checked against: the one this Admin started from (review W7). */
  readonly version: number;
  /** What "unsaved" is measured from. */
  readonly values: LetterForm;
}

/**
 * One letter, written on the left and printed on the right (Option A). Saved with "Save letter" against the version
 * the Admin started from; a save by someone else meanwhile is said plainly, and their version can be brought in with
 * these changes kept. "Jobs can choose this letter" retires a letter without removing it (#6).
 */
export function LetterEditor({
  letter,
  stashed,
  canEdit,
  knownIds,
  onLeave,
  onCreated,
}: {
  /** Null: a new letter. */
  readonly letter: OfferLetterView | null;
  readonly stashed: StashedLetter | undefined;
  readonly canEdit: boolean;
  /** Every letter id on the page, so a new letter's id can be told apart in the answer. */
  readonly knownIds: readonly string[];
  readonly onLeave: (draft: StashedLetter | null) => void;
  readonly onCreated: (id: string) => void;
}): ReactNode {
  const ventures = useVentures();
  const save = useSaveOfferLetter();
  const show = useToasts((state) => state.show);
  const formId = useId();
  const isOneColumn = useMediaQuery(ONE_COLUMN_QUERY);
  const [pane, setPane] = useState<Pane>('write');
  const server = letter ? formOf(letter) : NEW_LETTER;
  const [base, setBase] = useState<Base>(() => stashed ? { version: stashed.version, values: stashed.base } : { version: letter?.version ?? 0, values: server });
  const [submitted, setSubmitted] = useState<LetterForm | null>(null);
  const form = useForm<OfferLetterRequest, unknown, OfferLetterInput>({ resolver: zodResolver(offerLetterSchema), mode: 'onBlur', defaultValues: stashed?.values ?? base.values });
  const values: WatchedLetter = useWatch({ control: form.control });
  const changed = changedLetterParts(values, base.values);
  const isDirty = changed.length > 0;
  const body = useRef<HTMLTextAreaElement | null>(null);

  // Someone else saved this letter. As on Career site: ours, untouched or already the same is taken in; edits are kept
  // and the page says so.
  const isOurs = submitted !== null && changedLetterParts(server, submitted).length === 0;
  const isNewer = letter !== null && letter.version !== base.version;
  const isOutdated = isNewer && !isOurs && isDirty && changedLetterParts(values, server).length > 0;
  // Set while rendering (state derived from the letter); the form is reset before the screen paints.
  const [resetTo, setResetTo] = useState<LetterForm | null>(null);
  if (isNewer && !isOutdated) {
    setBase({ version: letter.version, values: server });
    if (isOurs) setSubmitted(null);
    if (!isOurs && !isDirty) setResetTo(server);
  }
  useLayoutEffect(() => {
    if (resetTo) form.reset(resetTo);
  }, [resetTo, form]);

  // Leaving this letter for another keeps its draft; the page puts it back on return.
  const leaving = useRef<StashedLetter | null>(null);
  const onLeaveRef = useRef(onLeave);
  useEffect(() => {
    leaving.current = isDirty ? { version: base.version, base: base.values, values: currentForm(form.getValues()) } : null;
    onLeaveRef.current = onLeave;
  });
  useEffect(() => () => onLeaveRef.current(leaving.current), []);

  const bringInTheirs = (): void => {
    if (!letter) return;
    form.reset(rebasedLetter(currentForm(form.getValues()), base.values, formOf(letter)));
    setBase({ version: letter.version, values: formOf(letter) });
  };

  const submit = form.handleSubmit(async (input) => {
    const sent = savedLetterOf(currentForm(input));
    setSubmitted(sent);
    try {
      const letters = await save.mutateAsync(letter ? { id: letter.id, version: base.version, letter: sent } : { id: null, letter: sent });
      show({ tone: 'success', message: `${sent.name} saved`, detail: 'Offers made from now on use this wording. Offers already made keep theirs.' });
      if (!letter) {
        const created = letters.find((row) => !knownIds.includes(row.id) && row.name === sent.name);
        // Found among the cards: open it (the page drops this draft). Not found: clear the form, so Save can't make it twice.
        if (created) onCreated(created.id);
        else form.reset(NEW_LETTER);
      }
    } catch (cause) {
      setSubmitted(null);
      show({ tone: 'danger', message: 'The letter was not saved', detail: errorMessage(cause) });
    }
  });

  /** Puts `{{field}}` where the cursor was in the letter, and the cursor after it. */
  const insert = (key: OfferMergeField): void => {
    const element = body.current;
    const current = form.getValues('body') ?? '';
    const next = insertField(current, element?.selectionStart ?? current.length, element?.selectionEnd ?? current.length, key);
    form.setValue('body', next.body, { shouldDirty: true });
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(next.cursor, next.cursor);
    });
  };

  /** 06-qol §2: every form saves on Ctrl or Cmd + Enter. */
  const onKeyDown = (event: KeyboardEvent<HTMLFormElement>): void => {
    if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();
    if (canEdit && isDirty && !isOutdated) void submit();
  };

  const errors = form.formState.errors;
  const unknown = unknownMergeFields(values.body ?? '');
  const bodyError = unknown.length > 0 ? `Not a letter field: ${unknown.map((name) => `{{${name}}}`).join(', ')}. Use “Insert a field”.` : errors.body?.message;
  const { ref: registerBody, ...bodyField } = form.register('body');
  const ventureOptions = [{ value: EVERY_VENTURE, label: 'Every venture' }, ...(ventures.data ?? []).map((venture) => ({ value: venture.id, label: venture.name }))];

  const writePane = (
    <form id={formId} className={styles.editor} onSubmit={(event) => void submit(event)} onKeyDown={onKeyDown} noValidate>
      <div className={styles.paneHead}>
        <h2 className={styles.paneTitle}>{letter ? letter.name : 'New letter'}</h2>
        {letter && <span className={styles.usage}>{usageText(letter)}</span>}
      </div>
      <fieldset className={styles.fieldset} disabled={!canEdit}>
        <div className={styles.grid}>
          <Field label="Name" isRequired error={errors.name?.message}>
            {({ inputId, describedBy, isInvalid }) => <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} maxLength={120} placeholder="Standard offer" {...form.register('name')} />}
          </Field>
          <Field label="Venture" isOptional>
            {({ inputId, describedBy }) => (
              <Controller
                control={form.control}
                name="ventureId"
                render={({ field }) => (
                  <Select
                    id={inputId}
                    describedBy={describedBy}
                    label="Venture"
                    value={field.value ?? EVERY_VENTURE}
                    options={ventureOptions}
                    disabled={!canEdit}
                    onChange={(value) => field.onChange(value === EVERY_VENTURE ? null : value)}
                  />
                )}
              />
            )}
          </Field>
          <Field label="Signed by" isRequired error={errors.signatoryName?.message}>
            {({ inputId, describedBy, isInvalid }) => <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} maxLength={120} placeholder="Nasreen Akter" {...form.register('signatoryName')} />}
          </Field>
          <Field label="Their designation" isRequired error={errors.signatoryDesignation?.message}>
            {({ inputId, describedBy, isInvalid }) => <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} maxLength={120} placeholder="Head of People" {...form.register('signatoryDesignation')} />}
          </Field>
        </div>
        <Field
          label="The letter"
          isRequired
          hint="A blank line starts a new paragraph. Fields fill in from each offer."
          action={canEdit ? <InsertFieldMenu onInsert={insert} /> : undefined}
          {...(bodyError ? { error: bodyError } : {})}
        >
          {({ inputId, describedBy, isInvalid }) => (
            <Textarea
              id={inputId}
              aria-describedby={describedBy}
              isInvalid={isInvalid}
              rows={16}
              maxLength={OFFER_LETTER_BODY_MAX}
              {...bodyField}
              ref={(element) => {
                registerBody(element);
                body.current = element;
              }}
            />
          )}
        </Field>
      </fieldset>
      <div className={styles.foot}>
        <Controller
          control={form.control}
          name="isActive"
          render={({ field }) => <Switch checked={field.value ?? true} onChange={field.onChange} disabled={!canEdit} label="Jobs can choose this letter" />}
        />
        <span className={styles.lastChange}>{letter ? `Last changed ${letter.updatedByName ? `by ${letter.updatedByName} · ` : ''}${formatDateTime(letter.updatedAt)}` : 'Not saved yet'}</span>
        {canEdit && (
          <span className={styles.actions}>
            {isDirty && (
              <Button size="sm" variant="quiet" disabled={save.isPending} onClick={() => form.reset(base.values)}>
                Discard
              </Button>
            )}
            <Button
              variant="solid"
              type="submit"
              isPending={save.isPending}
              disabled={!isDirty || isOutdated}
              aria-label={isDirty ? `Save letter — ${changed.length} unsaved ${changed.length === 1 ? 'change' : 'changes'}` : 'Save letter'}
            >
              Save letter
              {isDirty && <span className={styles.unsavedDot} aria-hidden />}
            </Button>
          </span>
        )}
      </div>
      {canEdit && <EditorStatus changed={changed} isOutdated={isOutdated} onBringIn={bringInTheirs} />}
    </form>
  );
  const previewPane = <LetterPreview values={values} letterId={letter?.id ?? null} />;

  if (isOneColumn) {
    return (
      <Tabs label={letter ? letter.name : 'New letter'} tabs={PANES} value={pane} onChange={setPane}>
        <div hidden={pane !== 'write'}>{writePane}</div>
        <div hidden={pane !== 'preview'}>{previewPane}</div>
      </Tabs>
    );
  }
  return (
    <div className={styles.columns}>
      {writePane}
      <div className={styles.previewColumn}>{previewPane}</div>
    </div>
  );
}

function EditorStatus({ changed, isOutdated, onBringIn }: { readonly changed: readonly string[]; readonly isOutdated: boolean; readonly onBringIn: () => void }): ReactNode {
  if (isOutdated) {
    return (
      <div className={styles.status} role="status">
        <span className={styles.outdated}>Someone else saved this letter while you were changing it. Bring in their version — your changes stay — then save.</span>
        <Button size="sm" variant="outline" onClick={onBringIn}>
          Bring in their version
        </Button>
      </div>
    );
  }
  if (changed.length === 0) return null;
  return (
    <p className={styles.status} role="status">
      <strong className={styles.strong}>
        {changed.length} unsaved {changed.length === 1 ? 'change' : 'changes'}
      </strong>
      {' · '}
      {changed.join(', ')}
    </p>
  );
}

/** "Used by 6 open jobs · 3 closed": an edit reaches the open ones only (closed jobs make no more offers). */
export function usageText(letter: Pick<OfferLetterView, 'openJobCount' | 'closedJobCount'>): string {
  const open = `Used by ${letter.openJobCount} open ${letter.openJobCount === 1 ? 'job' : 'jobs'}`;
  return letter.closedJobCount > 0 ? `${open} · ${letter.closedJobCount} closed` : open;
}

/** The form's values with every part filled, as RHF holds them before the schema's defaults apply. */
function currentForm(values: OfferLetterRequest): LetterForm {
  return {
    name: values.name,
    ventureId: values.ventureId ?? null,
    signatoryName: values.signatoryName,
    signatoryDesignation: values.signatoryDesignation,
    body: values.body,
    isActive: values.isActive ?? true,
  };
}
